import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { API } from "@/data/content";

const fmt = (n, dp) =>
  n.toLocaleString("en-AU", { minimumFractionDigits: dp, maximumFractionDigits: dp });

function Item({ name, price, change_pct, dp, suffix }) {
  const up = change_pct >= 0;
  return (
    <div className="flex items-center gap-3 shrink-0 px-5 sm:px-7 py-4" data-testid={`market-item-${name.replace(/\W+/g, "-").toLowerCase()}`}>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold whitespace-nowrap">{name}</div>
        <div className="font-display font-bold text-white text-sm sm:text-base mt-0.5 whitespace-nowrap">
          {fmt(price, dp)}{suffix && <span className="text-[10px] text-white/35 font-normal ml-1">{suffix}</span>}
        </div>
      </div>
      <span className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5 ${up ? "bg-brand/20 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
        {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
        {up ? "+" : ""}{change_pct.toFixed(2)}%
      </span>
    </div>
  );
}

export default function MarketTicker() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () =>
      fetch(`${API}/markets`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => alive && d && setData(d))
        .catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  if (!data || !data.groups) return null;
  const { indices = [], metals = [], crypto = [], fx = [] } = data.groups;
  if (!indices.length && !metals.length && !fx.length && !crypto.length) return null;

  const updated = new Date(data.updated_at).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });

  return (
    <section data-testid="market-ticker" className="relative bg-obsidian grain overflow-hidden border-y border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2.5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-gold">Market snapshot</span>
          </div>
          <span className="text-[10px] text-white/30" data-testid="market-updated">Live · updated {updated}</span>
        </div>

        <div className="flex overflow-x-auto divide-x divide-white/8 -mx-4 sm:mx-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {indices.map((m) => <Item key={m.symbol} {...m} dp={2} />)}
          {[...metals, ...crypto].map((m) => <Item key={m.symbol} {...m} dp={2} suffix="USD" />)}
          {fx.map((m) => <Item key={m.symbol} {...m} name={`AUD/${m.name}`} dp={4} />)}
        </div>

        <p className="text-[10px] text-white/25 mt-3">Indicative market data, may be delayed. Not financial advice.</p>
      </div>
    </section>
  );
}
